import { useState, useEffect, useCallback, useRef } from "react";
import { glowSuggestions, gratefulSuggestions } from "../constants/suggestions";

/**
 * useFloatingSuggestions — manages the floating suggestion bubbles and
 * colored spec particles shown on the camera overlay.
 *
 * Returns { suggestions, specs, hasUserInteracted, handleUserInteraction }
 */
export default function useFloatingSuggestions({
	postType,
	isContest,
	isCaptureDone,
	showCrop,
	autoTriggerUpload,
}) {
	const [floatingSuggestions, setFloatingSuggestions] = useState([]);
	const [floatingSpecs, setFloatingSpecs] = useState([]);
	const [hasUserInteracted, setHasUserInteracted] = useState(false);

	const handleUserInteraction = useCallback(() => {
		if (!hasUserInteracted) {
			setHasUserInteracted(true);
		}
	}, [hasUserInteracted]);

	// Clear all floating elements when user interacts
	useEffect(() => {
		if (hasUserInteracted) {
			const timeout = setTimeout(() => {
				setFloatingSuggestions([]);
				setFloatingSpecs([]);
			}, 500);
			return () => clearTimeout(timeout);
		}
	}, [hasUserInteracted]);

	// Auto-spawn floating suggestions for glow and grateful posts
	useEffect(() => {
		if (
			(postType !== "glow" && postType !== "grateful") ||
			isContest ||
			isCaptureDone ||
			showCrop ||
			autoTriggerUpload ||
			hasUserInteracted
		) {
			return;
		}

		const isGrateful = postType === "grateful";
		const minInterval = isGrateful ? 10000 : 2000;
		const maxInterval = isGrateful ? 10000 : 3000;
		const suggestionList = isGrateful ? gratefulSuggestions : glowSuggestions;

		const removalTimers = [];

		const spawnInterval = setInterval(
			() => {
				const randomSuggestion =
					suggestionList[Math.floor(Math.random() * suggestionList.length)];
				const newSuggestion = {
					id: Date.now() + Math.random(),
					text: randomSuggestion,
					left: Math.random() * 100,
					isGrateful,
				};

				setFloatingSuggestions((prev) => [...prev, newSuggestion]);

				const timer = setTimeout(() => {
					setFloatingSuggestions((prev) =>
						prev.filter((s) => s.id !== newSuggestion.id)
					);
				}, 15000);
				removalTimers.push(timer);
			},
			Math.random() * (maxInterval - minInterval) + minInterval
		);

		return () => {
			clearInterval(spawnInterval);
			removalTimers.forEach(clearTimeout);
		};
	}, [
		postType,
		isContest,
		isCaptureDone,
		showCrop,
		autoTriggerUpload,
		hasUserInteracted,
	]);

	// Auto-spawn floating colored specs
	useEffect(() => {
		if (isCaptureDone || showCrop || autoTriggerUpload || hasUserInteracted) {
			return;
		}

		const specTimers = [];

		const specInterval = setInterval(() => {
			const getSpecColor = () => {
				if (isContest) {
					return Math.random() > 0.5 ? "blue" : "orange";
				}
				return postType === "grateful" ? "green" : "orange";
			};

			const newSpec = {
				id: Date.now() + Math.random(),
				left: Math.random() * 100,
				size: Math.random() * 6 + 4,
				delay: Math.random() * 2,
				color: getSpecColor(),
			};

			setFloatingSpecs((prev) => [...prev, newSpec]);

			const timer = setTimeout(() => {
				setFloatingSpecs((prev) => prev.filter((s) => s.id !== newSpec.id));
			}, 12000);
			specTimers.push(timer);
		}, 500);

		return () => {
			clearInterval(specInterval);
			specTimers.forEach(clearTimeout);
		};
	}, [
		isContest,
		postType,
		isCaptureDone,
		showCrop,
		autoTriggerUpload,
		hasUserInteracted,
	]);

	return {
		suggestions: floatingSuggestions,
		specs: floatingSpecs,
		hasUserInteracted,
		handleUserInteraction,
	};
}
